import { writeFileSync, readFileSync, existsSync, chmodSync, unlinkSync } from "node:fs";
import { resolve } from "node:path";
import type { CaiConfig } from "./types.js";

const HOOK_MARKER = "# cai-drift-check-start";
const HOOK_END_MARKER = "# cai-drift-check-end";

// Exported so doctor.ts can inspect hook state without re-reading internals
export const HOOK_MARKERS = { start: HOOK_MARKER, end: HOOK_END_MARKER, legacy: "# cai-drift-check" };

function buildHookContent(config: CaiConfig, autoFix: boolean, threshold = 80): string {
  const cliPath = resolve(config.scaffoldRoot, "dist", "cli.js");
  const base = existsSync(cliPath) ? `node "${cliPath}"` : "npx @temroi/cai";
  const checkCmd = `${base} check --quiet --incremental`;
  const fixCmd = `${base} fix 2>&1`;
  const sessionCmd = `${base} session 2>&1`;
  const installedAt = new Date().toISOString().slice(0, 10);

  const autoFixBlock = autoFix ? `
# Auto-fix: if score below ${threshold}, run safe fixes and refresh session context
SCORE_NUM=$(echo "$SCORE" | grep -oE '[0-9]+/100' | cut -d/ -f1 || true)
if [ -n "$SCORE_NUM" ] && [ "$SCORE_NUM" -lt ${threshold} ]; then
  echo "cai: score $SCORE_NUM/100 — running auto-fix..."
  ${fixCmd}
  ${sessionCmd}
fi` : "";

  return `#!/bin/sh
${HOOK_MARKER}
# Installed by cai watch on ${installedAt}
# What this does: runs a drift check after every commit to keep scaffold in sync.
# If nothing is out of sync, no output is shown.
# To remove: run 'cai watch --uninstall'
# To bypass once: git commit --no-verify
# To disable all cai hooks: set CAI_HOOKS=0 in your environment
[ "\${CAI_HOOKS:-1}" = "0" ] && exit 0
SCORE=$(${checkCmd} 2>&1) || true
case "$SCORE" in
  *"100/100"*) ;;
  *) echo "$SCORE" ;;
esac
${autoFixBlock}
${HOOK_END_MARKER}
`;
}

export async function manageHook(
  config: CaiConfig,
  opts: { uninstall?: boolean; autoFix?: boolean; threshold?: number }
): Promise<void> {
  const hookPath = resolve(config.projectRoot, ".git", "hooks", "post-commit");

  if (opts.uninstall) {
    uninstallHook(hookPath);
    return;
  }

  installHook(hookPath, config, opts.autoFix ?? false, opts.threshold);
}

function installHook(hookPath: string, config: CaiConfig, autoFix: boolean, threshold?: number): void {
  const hookContent = buildHookContent(config, autoFix, threshold);

  if (existsSync(hookPath)) {
    const existing = readFileSync(hookPath, "utf-8");
    if (existing.includes(HOOK_MARKER)) {
      console.log("⚠ Hook already installed — nothing changed.");
      console.log("  ℹ To reinstall, run: cai watch --uninstall && cai watch");
      return;
    }

    // Append to existing hook
    const updated = existing.trimEnd() + "\n\n" + hookContent;
    writeFileSync(hookPath, updated);
    chmodSync(hookPath, 0o755);
    console.log("✔ Added drift check to your existing post-commit hook.");
    console.log("  ℹ After each commit, cai will check whether your scaffold is still accurate.");
    return;
  }

  writeFileSync(hookPath, hookContent);
  chmodSync(hookPath, 0o755);
  console.log("✔ post-commit hook installed.");
  console.log("  ℹ After each commit, cai will run a drift check automatically.");
  console.log("  ℹ Nothing is shown when everything is in sync — output only appears when there is drift.");
  if (autoFix) {
    console.log(`  ℹ Auto-fix is on — if the score drops below ${threshold ?? 80}/100, cai fix will run and refresh your session context.`);
  }
  console.log("  ℹ To bypass a single commit: git commit --no-verify");
  console.log("  ℹ To disable all cai hooks:  CAI_HOOKS=0 git commit");
  console.log("  ℹ To remove the hook:        cai watch --uninstall");
}

function uninstallHook(hookPath: string): void {
  if (!existsSync(hookPath)) {
    console.log("⚠ No post-commit hook found — nothing to remove.");
    return;
  }

  const content = readFileSync(hookPath, "utf-8");
  // Support both current marker and legacy marker (legacy is a shorter prefix of the current one)
  if (!content.includes(HOOK_MARKER) && !content.includes(HOOK_MARKERS.legacy)) {
    console.log("⚠ A post-commit hook exists but was not installed by cai — not modified.");
    console.log("  ℹ To remove it manually, delete or edit: .git/hooks/post-commit");
    return;
  }

  // Remove everything between start and end markers (inclusive)
  const lines = content.split("\n");
  const filtered: string[] = [];
  let inCaiBlock = false;

  for (const line of lines) {
    if (line.includes(HOOK_MARKER)) {
      inCaiBlock = true;
      continue;
    }
    if (inCaiBlock && line.includes(HOOK_END_MARKER)) {
      inCaiBlock = false;
      continue;
    }
    if (!inCaiBlock) {
      filtered.push(line);
    }
  }

  const remaining = filtered.join("\n").trim();
  if (remaining === "#!/bin/sh" || remaining === "") {
    // Only shebang left — remove the file
    unlinkSync(hookPath);
    console.log("✔ post-commit hook removed.");
  } else {
    writeFileSync(hookPath, remaining + "\n");
    chmodSync(hookPath, 0o755);
    console.log("✔ Removed cai section from post-commit hook. Your other hook scripts are untouched.");
  }
}
